/* eslint-disable import/named */
import { computed, Injectable, signal } from '@angular/core';
import { assign, unionWith } from 'lodash';
import {
  Applicant,
  ProfessionalExperienceCompany,
  Qualification,
  Resume,
  ResumeListTypeEnum,
} from '../../../../../models/applicant.model';
import { DateUtils } from 'ats-ui-lib';
import {
  UploadEvents,
  UploadEventTypeEnum,
} from '../../../../../root/resume-upload-events/resume-upload-events.model';
import { ResumeUploadEventsService } from '../../../../../root/resume-upload-events/resume-upload-events.service';

type ExtendedUploadEvents = UploadEvents & { isValid: boolean };

@Injectable({
  providedIn: 'root',
})
export class ApplicantLinkedinStateService {
  private resumeData = signal<Applicant | null>(null);

  public updatedData = computed(() => {
    return this.resumeData();
  });

  public linkedinFileEvent = signal<ExtendedUploadEvents | null>(null);

  constructor(private uploadEventsService: ResumeUploadEventsService) {}

  public setData(applicant: Applicant): void {
    this.formatProfessionalExperience(applicant);
    delete applicant.personalData.email;
    this.resumeData.set(applicant);
  }

  public updateData(applicant: Partial<Applicant>): void {
    const mergedData = assign(this.resumeData(), applicant);
    this.resumeData.set(mergedData);
  }

  public updateList(
    list: ProfessionalExperienceCompany[] | Qualification[],
    type: ResumeListTypeEnum,
  ): void {
    const applicant = this.resumeData();

    if (!applicant) return;

    if (!applicant.resume) return;

    switch (type) {
      case ResumeListTypeEnum.Experience:
        applicant.resume.professionalExperience = list as ProfessionalExperienceCompany[];
        break;
      case ResumeListTypeEnum.Qualification:
        applicant.resume.qualifications = list as Qualification[];
        break;
    }

    this.resumeData.set(applicant);
  }

  public getData(): Applicant | null {
    return this.resumeData();
  }

  public clearData(): void {
    this.resumeData.set(null);
    this.linkedinFileEvent.set(null);
    this.uploadEventsService.reset(UploadEventTypeEnum.ResumeLinkedin);
  }

  public mergeArrays(resume: Resume, linkedin: Resume, key: string): Resume | void {
    if (key === 'resume') {
      if (!resume || !linkedin) return;
      this.mergeExperiences(resume, linkedin);
      this.mergeQualifications(resume, linkedin);

      return resume;
    }

    return undefined;
  }

  private mergeExperiences(resume: Resume, linkedin: Resume): void {
    let resumeExperiences = resume.professionalExperience || [];
    const linkedinExperiences = linkedin.professionalExperience || [];

    if (Array.isArray(resumeExperiences) && Array.isArray(linkedinExperiences)) {
      resume.professionalExperience = unionWith(
        resume.professionalExperience,
        linkedinExperiences,
        (a: ProfessionalExperienceCompany, b: ProfessionalExperienceCompany) => a.name === b.name,
      ).map((company) => {
        const matchingCompany = linkedinExperiences.find((c) => c.name === company.name);

        if (matchingCompany) {
          return {
            ...company,
            positions: unionWith(
              company.positions || [],
              matchingCompany.positions || [],
              (posA, posB) => posA.name === posB.name,
            ),
          };
        }

        return company;
      });

      resume.professionalExperience.forEach((company) => {
        company.positions.forEach((pos) => {
          pos.startDate = DateUtils.transformPoDatePickerOutputToApiFormat(pos.startDate);
          pos.endDate = pos.endDate
            ? DateUtils.transformPoDatePickerOutputToApiFormat(pos.endDate)
            : null;
        });
      });
    }
  }

  private mergeQualifications(resume: Resume, linkedin: Resume): void {
    let resumeQualifications = resume.qualifications || [];
    const linkedinQualifications = linkedin.qualifications || [];

    if (Array.isArray(resumeQualifications) && Array.isArray(linkedinQualifications)) {
      resume.qualifications = unionWith(
        resumeQualifications,
        linkedinQualifications,
        (a: Qualification, b: Qualification) =>
          a.institutionName === b.institutionName && a.courseName === b.courseName,
      );
    }
  }

  private formatProfessionalExperience(applicant: Applicant): void {
    applicant.resume?.professionalExperience?.forEach((company) => {
      company.positions.forEach((pos) => {
        pos.startDate = DateUtils.transformPoDatePickerOutputToApiFormat(pos.startDate);
        pos.endDate = pos.endDate
          ? DateUtils.transformPoDatePickerOutputToApiFormat(pos.endDate)
          : null;
      });
    });
  }
}
